import { Icon, TextLink, Tooltip } from "@components/library";
import { COLORS, FONTS } from "@constants";
import { RefObject, useEffect, useState } from "react";
import styled from "styled-components";

export interface RequestSidebarItem<T extends string = string> {
  key: T;
  ref: RefObject<HTMLElement>;
  title: string;
}

const defaultRootMargin = "-40% 0px -40% 0px";

export interface RequestSidebarProps<T extends string = string> {
  items: Array<RequestSidebarItem<T>>;
  blockedKeys?: T[];
  onItemClick?: (key: T) => void;
  /** Optional handler to open the standard sign up/sign in flow */
  onSignupClick?: () => void;
  /**
   * IntersectionObserver rootMargin used to determine active section.
   */
  rootMargin?: string;
  /** Sticky top offset (px) for the sidebar list */
  topOffset?: number;
}

export default function RequestSidebar<T extends string = string>({
  items,
  blockedKeys = [],
  onItemClick,
  onSignupClick,
  rootMargin = defaultRootMargin,
  topOffset = 20,
}: RequestSidebarProps<T>) {
  const [focusedKey, setFocusedKey] = useState<T | null>(null);

  const buildObserver = (ref: RefObject<HTMLElement>, key: T) => {
    if (!ref?.current) return undefined;
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setFocusedKey(key);
        });
      },
      { rootMargin, threshold: [0, 1] }
    );
  };

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const add = (ref: RefObject<HTMLElement>, key: T) => {
      const obs = buildObserver(ref, key);
      if (obs && ref.current) {
        obs.observe(ref.current);
        observers.push(obs);
      }
    };

    items.forEach(({ ref, key }) => add(ref, key));

    return () => observers.forEach((o) => o.disconnect());
  }, [items, rootMargin]);

  const blockedSet = new Set(blockedKeys);

  return (
    <List topOffset={topOffset}>
      {items.map(({ key, ref, title }) => {
        const isBlocked = blockedSet.has(key);

        if (!isBlocked) {
          return (
            <Item
              key={key}
              isFocused={key === focusedKey}
              isBlocked={false}
              onClick={() => {
                setFocusedKey(key);
                onItemClick?.(key);
                // Ensure focus remains after scroll animations in some browsers
                setTimeout(() => setFocusedKey(key), 300);
                ref.current?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {title}
            </Item>
          );
        }

        return (
          <Item
            key={key}
            isFocused={key === focusedKey}
            isBlocked={true}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <Tooltip
              content={
                <div>
                  <TextLink
                    to="#"
                    onClick={() => {
                      onSignupClick?.();
                    }}
                    color={COLORS.WHITE}
                    font={FONTS.REGULAR_3}
                  >
                    Sign up
                  </TextLink>{" "}
                  to access.
                </div>
              }
              position="bottom"
              asPortal
              contentWidth="100%"
            >
              <ItemRow>
                <Icon name="Lock" size="xs" margin="0 8px 0 0" />
                {title}
              </ItemRow>
            </Tooltip>
          </Item>
        );
      })}
    </List>
  );
}

export const List = styled.ul<{ topOffset: number }>`
  position: sticky;
  top: ${({ topOffset }) => `${topOffset}px`};

  width: 100%;
  padding: 0;

  list-style-type: none;
  ${FONTS.MEDIUM_2}

  li {
    padding: 8px;
  }

  li:not(:last-child) {
    border-bottom: 1px solid ${COLORS.NEUTRAL_200};
  }
`;

export const Item = styled.li<{ isFocused: boolean; isBlocked: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ isFocused }) => (isFocused ? COLORS.HALO_BLUE : COLORS.BLACK)};
  opacity: ${({ isBlocked }) => (isBlocked ? 0.9 : 1)};

  &:hover,
  &:active,
  &:focus {
    color: ${COLORS.BLACK};
    text-decoration: none;
    outline: none;
  }

  &:hover {
    cursor: pointer;
    color: ${COLORS.BLUE_900};
  }
`;

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
`;
